// HTTP client + persisted request state for face claims ("בעריכה" holds).
//
// Every call takes the event object from getFaceClaimEvent() (faceClaimEvent.js) and refuses to touch
// the network without one. Results are normalized to { ok, status, code, body } and NEVER throw: a
// network failure is `code: "network"`. The server contract is documented in
// minime-web app/utils/face_claims.py / app/routes/face_claim_routes.py.
import { v4 as uuidv4 } from "uuid";
import { SERVER_URL } from "../../../config/serverUrl";
import { getActiveGuestId, readStoredGuestId } from "../../../eventIdentity";

export const newRequestId = () => uuidv4();

// ---- persisted per-event claim state (own keys, explicitly managed) -------------------------------------
// { token, faces, acqRid, acqKey, acqPrior, approveRid, approveAt, guest }
//
// `guest` binds the record (and the flow deadline, see useFlowInactivity) to the guest id of the tab that
// wrote it (minted fresh by the gate / Home). A full document navigation need not run React cleanup, so a
// retained record of an OLD guest can still be there on a deep entry: it reads as absent for any other
// guest (never sends the old token under a new UserId). The same guest reloading keeps it. No storage is
// cleared for this: a stale record is simply overwritten by the next write.
const claimKey = (eventName) => `faceClaim:${eventName}`;
export const flowKey = (eventName) => `faceFlow:${eventName}`;

// ONE source for the guest id of every claim call: the in-memory register that the gate / Home set in the
// same statement as the React context id the approve sends (eventIdentity.setActiveGuestId), with
// sessionStorage["user_id"] only as the carrier across a reload (the gate re-registers it before any
// guest page renders). On 2026-10-18 the acquire read storage alone, found no key on some devices, and
// every approve of those guests was refused as guest_mismatch.
export const currentGuestId = () => getActiveGuestId() || readStoredGuestId() || "";

// Diagnostic sent with the acquire, so the server can count the devices whose storage does not agree with the
// id actually sent: "storage" = storage holds the id that is sent (the register agrees, or the register is empty),
// "memory" = storage has no key and the register carried the id, "mismatch" = storage holds ANOTHER id (a restored
// or foreign copy); the register's id is what is sent in every case.
export const currentGuestSource = () => {
  const stored = readStoredGuestId();
  const registered = getActiveGuestId();
  if (!stored) return "memory";
  if (registered && stored !== registered) return "mismatch";
  return "storage";
};

export const readClaimState = (eventName) => {
  try {
    const raw = sessionStorage.getItem(claimKey(eventName));
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.guest !== currentGuestId()) return null; // another guest's (or unowned) record
    return parsed;
  } catch (error) {
    return null;
  }
};

export const writeClaimState = (eventName, state) => {
  try {
    sessionStorage.setItem(claimKey(eventName), JSON.stringify({ ...state, guest: currentGuestId() }));
  } catch (error) {
    // storage unavailable: the in-memory flow still works, a reload just loses the hold handle
  }
};

export const clearClaimState = (eventName) => {
  try {
    sessionStorage.removeItem(claimKey(eventName));
  } catch (error) {
    // nothing to clear
  }
};

export const clearFlowState = (eventName) => {
  try {
    sessionStorage.removeItem(flowKey(eventName));
  } catch (error) {
    // nothing to clear
  }
};

// ---- requests --------------------------------------------------------------------------------------------------

const NO_EVENT = { ok: false, status: 0, code: "no_event", body: {} };

const NETWORK = { ok: false, status: 0, code: "network", body: {} };

// timeoutMs (optional) bounds the WHOLE exchange (headers + body): a request that never settles resolves
// as `network`, so poll loops keep going instead of waiting forever. Aborts the fetch where supported.
const call = async (event, path, { method = "POST", body, headers, keepalive, timeoutMs } = {}) => {
  if (!event) return NO_EVENT; // no validated identity => no claim request
  const controller = timeoutMs && typeof AbortController !== "undefined" ? new AbortController() : null;
  let timer = null;
  const exchange = (async () => {
    const response = await fetch(`${SERVER_URL}${path}`, {
      method,
      headers: { "Content-Type": "application/json", ...(headers || {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...(keepalive ? { keepalive: true } : {}),
      ...(controller ? { signal: controller.signal } : {}),
    });
    const json = await response.json().catch(() => ({}));
    const parsed = json && typeof json === "object" ? json : {};
    return {
      ok: Boolean(response.ok) && parsed.success !== false,
      status: response.status,
      code: parsed.code || (response.ok ? "ok" : "error"),
      body: parsed,
    };
  })();
  try {
    if (!timeoutMs) return await exchange;
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => {
        if (controller) controller.abort();
        resolve(NETWORK);
      }, timeoutMs);
    });
    exchange.catch(() => {}); // a late rejection after the timeout is not unhandled
    return await Promise.race([exchange, timeout]);
  } catch (error) {
    return NETWORK;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export const STATUS_POLL_TIMEOUT_MS = 15000;

// `timeoutMs` (optional) bounds the exchange: the result page's re-acquire after a lost approve uses it, so a dead
// network cannot keep the guest on the saving screen (the gallery's own acquire stays unbounded, as before).
export const acquireFaces = (event, { faces, requestId, claimToken, userId, guestSource, timeoutMs }) =>
  call(event, "/api/face-claims/acquire", {
    timeoutMs,
    body: {
      event_name: event && event.eventName,
      faces,
      request_id: requestId,
      UserId: userId,
      ...(guestSource ? { guest_source: guestSource } : {}),
      ...(claimToken ? { claim_token: claimToken } : {}),
    },
  });

// A renew that never completes (headers or body) must not own the single renewal slot (claimRenewal.js) forever:
// it resolves `network` after this bound, shorter than the 15 s renew throttle, so the next real activity retries.
export const RENEW_TIMEOUT_MS = 10000;

export const renewClaim = (event, claimToken) =>
  call(event, "/api/face-claims/renew", {
    timeoutMs: RENEW_TIMEOUT_MS,
    body: { event_name: event && event.eventName, claim_token: claimToken },
  });

// keepalive lets a release survive the navigation/unload that usually triggers it.
export const releaseClaim = (event, claimToken, { keepalive = false } = {}) =>
  call(event, "/api/face-claims/release", {
    body: { event_name: event && event.eventName, claim_token: claimToken },
    keepalive,
  });

// Held ready-paths for the gallery overlay; `claimToken` (header) adds `mine`.
export const fetchHeld = (event, claimToken, { timeoutMs } = {}) =>
  call(event, `/api/face-claims?event_name=${encodeURIComponent((event && event.eventName) || "")}`, {
    method: "GET",
    headers: claimToken ? { "X-Face-Claim": claimToken } : undefined,
    timeoutMs,
  });

export const fetchApproveStatus = (event, { requestId, claimToken }) =>
  call(event, "/api/face-claims/status", {
    timeoutMs: STATUS_POLL_TIMEOUT_MS,
    body: {
      event_name: event && event.eventName,
      ...(requestId ? { request_id: requestId } : {}),
      ...(claimToken ? { claim_token: claimToken } : {}),
    },
  });

// Best-effort release of whatever hold this tab persisted (Home start, timeout, cancel). Releases
// EDITING only: the server keeps processing/used/blocked exactly as they are. Never awaited by callers.
export const releaseStoredClaim = (event) => {
  if (!event) return Promise.resolve(NO_EVENT);
  const state = readClaimState(event.eventName);
  clearClaimState(event.eventName);
  if (!state || !state.token) return Promise.resolve({ ok: true, status: 0, code: "none", body: {} });
  return releaseClaim(event, state.token, { keepalive: true });
};

// The approve request carries the persisted idempotency id so a lost response / reload retry is a repeat.
//
// Returns { approveRid, approveAt }. `approveAt` (epoch ms) is the moment the id was minted, i.e. the guest's first
// approve tap of this claim, persisted with it: the server's editing lease on the chosen faces is counted from that
// tap and is not renewed afterwards (claimRenewal.js refuses a token whose approveRid is stored), so a later tap that
// reuses the stored id (a reload while the approve was in flight) must not restart the client's lease clock.
// A record written before approveAt existed has none: it comes back as null (lease clock unknown).
export const ensureApproveRequest = (eventName) => {
  const state = readClaimState(eventName) || {};
  if (!state.approveRid) {
    state.approveRid = newRequestId();
    state.approveAt = Date.now();
    writeClaimState(eventName, state);
  }
  return {
    approveRid: state.approveRid,
    approveAt: typeof state.approveAt === "number" ? state.approveAt : null,
  };
};

export const ensureApproveRequestId = (eventName) => ensureApproveRequest(eventName).approveRid;
